import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../Modal';
import { Donut, HBarChart, formatBytes, formatNumber } from '../charts';
import { Badge, Icon, Skeleton } from '../ui';
import { loadOverCap, loadWorkspaces } from '../../observability/client';

/* ── Workspace usage (Platform, Network and storage, Agent workspaces) ──────────
   One view, two scopes:
     · platform-wide, drawn above the shard table (`WorkspaceUsage`);
     · one shard, in a dialog opened from the shard's id (the default export).
   Each shows how many users sit in each band of their own cap (0-20 % … 80-100 %, and
   100 %+ for those over it), then the ten largest workspaces. The 100 %+ row opens the
   users over their cap, furthest over first, 15 a page.
   The figures are the control-plane's measurement of each user's folder
   (backend/services/workspace_usage.py), not EFS's per-filesystem `SizeInBytes`, which
   cannot tell one directory from another. So they lag by up to one measuring interval for
   an active user, and will not match the shard's Stored column to the byte. */

const TOP = 10;

// One colour per band, cool to hot; over the cap is the darkest.
const BAND_COLORS = ['#2ca02c', '#8fbf3f', '#f2c12e', '#f28e2b', '#d62728', '#7f1416'];
const OVER_PAGE = 15;
const bandName = (b) => (b.to_pct == null ? `${b.from_pct}%+ (over the cap)` : `${b.from_pct}–${b.to_pct}%`);

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const pct = (v) => `${v >= 10 ? v.toFixed(0) : v.toFixed(1)}%`;

function TopList({ users, onOpenUser }) {
  return (
    <HBarChart
      data={users.map(u => ({ ...u, name: u.username, count: u.bytes_used }))}
      nameWidth={170}
      valueWidth={190}
      formatVal={(v, row) => {
        const ofCap = row.cap_pct != null ? ` · ${pct(row.cap_pct)} of cap` : '';
        return `${formatBytes(v)}${row.status === 'partial' ? '+' : ''}${ofCap}`;
      }}
      onClick={row => onOpenUser(row.user_id)}
      emptyText="No workspace measured yet."
    />
  );
}

function CapBands({ data, onShowOver }) {
  const bands = data.cap_buckets || [];
  const slices = bands.map((b, i) => ({ name: bandName(b), value: b.users, color: BAND_COLORS[i] }));
  const capped = bands.reduce((sum, b) => sum + b.users, 0);
  return (
    <div className="workspace-usage-summary">
      <Donut data={slices} height={200} legend={false} formatVal={v => `${formatNumber(v)} users`}
             centerLabel={formatNumber(capped)} centerSub="users" />
      <div>
        <table className="table table-compact workspace-bands">
          <thead>
            <tr>
              <th scope="col">Of their cap</th>
              <th scope="col" className="text-right">Users</th>
              <th scope="col" className="text-right">Share</th>
            </tr>
          </thead>
          <tbody>
            {bands.map((b, i) => {
              const over = b.to_pct == null;
              return (
                <tr key={b.key}>
                  <td>
                    <span className="row gap-2">
                      <span className="chart-legend-swatch" style={{ background: BAND_COLORS[i] }} aria-hidden="true" />
                      {bandName(b)}
                    </span>
                  </td>
                  <td className="text-right num">
                    {over && b.users > 0 ? (
                      <button type="button" className="btn btn-link num" onClick={onShowOver}
                              aria-label={`Show the ${b.users} users over their cap`}>
                        {formatNumber(b.users)}
                      </button>
                    ) : formatNumber(b.users)}
                  </td>
                  <td className="text-right num">{capped ? pct((b.users * 100) / capped) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="row between gap-2 mt-2 text-sm text-muted">
          <span>
            {`${formatBytes(data.total_bytes)} in ${formatNumber(data.measured_users)} workspaces`}
            {data.uncapped_users > 0 && ` · ${formatNumber(data.uncapped_users)} without a cap`}
          </span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onShowOver}
                  disabled={!data.over_cap_users}>
            Users over their cap ({formatNumber(data.over_cap_users || 0)})
          </button>
        </div>
      </div>
    </div>
  );
}

function OverCapModal({ open, onClose, onBack, fsId, label, onOpenUser }) {
  const [offset, setOffset] = useState(0);
  const [state, setState] = useState({ data: null, error: null, loading: false });

  const fetchPage = useCallback(async (at) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      setState({ data: await loadOverCap({ fsId, offset: at, limit: OVER_PAGE }), error: null, loading: false });
    } catch (e) {
      setState({ data: null, error: e.message, loading: false });
    }
  }, [fsId]);

  useEffect(() => { if (open) fetchPage(offset); }, [open, offset, fetchPage]);
  useEffect(() => { if (!open) setOffset(0); }, [open]);

  const d = state.data;
  const last = d ? Math.min(offset + d.users.length, d.total) : 0;
  return (
    <Modal open={open} onClose={onClose} title={label ? `Users over their quota on ${label}` : 'Users over their quota, platform-wide'}
           maxWidth={820}
           description="Ordered by usage of their quota, highest first. Their writes are not blocked on EFS yet.">
      {state.error && (
        <div role="alert" className="notice notice-danger">
          <div className="notice-body">Could not load the users over their cap: {state.error}</div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => fetchPage(offset)}>Retry</button>
        </div>
      )}
      {!d && !state.error && <div role="status" aria-busy="true"><Skeleton height={14} /><Skeleton height={14} /></div>}
      {d && d.total === 0 && <div className="tile-empty">No user is over their quota.</div>}
      {d && d.total > 0 && (
        <div className="table-container">
          <table className="table table-compact">
            <thead>
              <tr>
                <th scope="col">User</th>
                <th scope="col">Tier</th>
                <th scope="col" className="text-right">Used</th>
                <th scope="col" className="text-right">Quota</th>
                <th scope="col" className="text-right">Usage of quota</th>
                <th scope="col">Measured</th>
              </tr>
            </thead>
            <tbody>
              {d.users.map(u => (
                <tr key={u.user_id}>
                  <td>
                    <button type="button" className="btn btn-link" onClick={() => onOpenUser(u.user_id)}>
                      {u.username}
                    </button>
                  </td>
                  <td>{u.tier_name || <span className="text-faint">No tier</span>}
                    {u.cap_source === 'user' && <span className="cell-sub">own quota</span>}</td>
                  <td className="text-right num">{formatBytes(u.bytes_used)}{u.status === 'partial' ? '+' : ''}</td>
                  <td className="text-right num">{formatBytes(u.cap_bytes)}</td>
                  <td className="text-right num"><Badge tone="danger">{pct(u.cap_pct)}</Badge></td>
                  <td className="text-sm">{when(u.measured_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="modal-footer row between gap-2">
        {onBack
          ? <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>Back to the shard</button>
          : <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>}
        {d && d.total > OVER_PAGE && (
          <div className="row gap-2">
            <span className="text-sm text-muted num">{`${offset + 1}–${last} of ${formatNumber(d.total)}`}</span>
            <button type="button" className="btn btn-secondary btn-sm" disabled={offset === 0 || state.loading}
                    onClick={() => setOffset(Math.max(0, offset - OVER_PAGE))}>Previous</button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={last >= d.total || state.loading}
                    onClick={() => setOffset(offset + OVER_PAGE)}>Next</button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function useUsage(fsId, active) {
  const [state, setState] = useState({ data: null, error: null, loading: false });
  const fetchUsage = useCallback(async () => {
    setState(st => ({ ...st, loading: true, error: null }));
    try {
      setState({ data: await loadWorkspaces({ fsId, limit: TOP }), error: null, loading: false });
    } catch (e) {
      setState({ data: null, error: e.message, loading: false });
    }
  }, [fsId]);
  useEffect(() => { if (active) fetchUsage(); }, [active, fetchUsage]);
  return [state, fetchUsage];
}

function UsageBody({ state, retry, shard, onShowOver, onOpenUser }) {
  const d = state.data;
  return (
    <>
      {state.loading && !d && (
        <div role="status" aria-busy="true" className="stack gap-3">
          <Skeleton height={180} />
          <Skeleton height={14} /><Skeleton height={14} /><Skeleton height={14} />
        </div>
      )}
      {state.error && (
        <div role="alert" className="notice notice-danger">
          <div className="notice-body">Could not load workspace usage: {state.error}</div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={retry}>Retry</button>
        </div>
      )}
      {d && d.measured_users === 0 && (
        <div className="tile-empty">
          {d.unmeasured_users
            ? `No workspace measured yet. ${formatNumber(d.unmeasured_users)} are waiting for the `
              + 'control-plane\'s first pass, which runs every '
              + `${Math.round(d.interval_s / 60)} minutes.`
            : (shard ? 'No workspace on this shard.' : 'No workspace yet.')}
        </div>
      )}
      {d && d.measured_users > 0 && (
        <>
          {d.caps_enabled && <CapBands data={d} onShowOver={onShowOver} />}
          <h4 className={`overline mb-2${d.caps_enabled ? ' mt-4' : ''}`}>
            {shard ? `Top ${TOP} users on this shard` : `Top ${TOP} users platform-wide`}
          </h4>
          <TopList users={d.users} onOpenUser={onOpenUser} />
          <p className="text-muted text-sm mt-3 mb-0">
            {`${formatNumber(d.measured_users)} workspaces hold ${formatBytes(d.total_bytes)}`}
            {d.unmeasured_users > 0 && `; ${formatNumber(d.unmeasured_users)} not measured yet`}
            {'. Active users are re-measured every '}
            {`${Math.round(d.interval_s / 60)} minutes, everyone else once a day.`}
            {d.partial_users > 0 && ' A + marks a folder whose count stopped at its budget: the real size is larger.'}
            {!d.caps_enabled && ' Storage quotas are off on this environment, so there are no bands.'}
          </p>
        </>
      )}
    </>
  );
}

/* Platform-wide, drawn in a card above the shard table. */
export function WorkspaceUsage() {
  const navigate = useNavigate();
  const [state, retry] = useUsage(undefined, true);
  const [showOver, setShowOver] = useState(false);
  return (
    <div className="card mb-4">
      <OverCapModal open={showOver} onClose={() => setShowOver(false)}
                    onOpenUser={(id) => { setShowOver(false); navigate(`/users/${id}`); }} />
      <div className="card-header">
        <h2 className="card-title">Usage across every shard</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={retry}><Icon name="refresh" /> Refresh</button>
      </div>
      <UsageBody state={state} retry={retry} shard={false} onShowOver={() => setShowOver(true)}
                 onOpenUser={(id) => navigate(`/users/${id}`)} />
    </div>
  );
}

/* One shard, in a dialog opened from its id in the shard table. */
export default function WorkspaceUsageModal({ open, onClose, fsId, shardLabel }) {
  const navigate = useNavigate();
  const [state, retry] = useUsage(fsId, open);
  // The over-cap list replaces this dialog while it is open, and Back returns to it.
  const [showOver, setShowOver] = useState(false);
  useEffect(() => { if (!open) setShowOver(false); }, [open]);
  const openUser = (id) => { onClose(); navigate(`/users/${id}`); };
  return (
    <>
      <OverCapModal open={open && showOver} onClose={onClose} onBack={() => setShowOver(false)}
                    fsId={fsId} label={shardLabel || fsId} onOpenUser={openUser} />
      <Modal open={open && !showOver} onClose={onClose} title={`Workspace usage on ${shardLabel || fsId}`}
             maxWidth={820}
             description="How full each user's workspace is against their own quota, and the ten largest.">
        <UsageBody state={state} retry={retry} shard onShowOver={() => setShowOver(true)}
                   onOpenUser={openUser} />
      </Modal>
    </>
  );
}
